import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Home from './Home';
import AllCourses from './AllCourses';
import TelemetryPage from './TelemetryPage';
import HomeHealth from './HomeHealth';

const MainPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState('home');
    const navigate = useNavigate();
    const location = useLocation();

    // Update active tab based on current route
    useEffect(() => {
        const path = location.pathname;
        if (path === '/all-courses') {
            setActiveTab('all-courses');
        } else if (path === '/telemetry') {
            setActiveTab('telemetry');
        } else if (path === '/home-health') {
            setActiveTab('home-health');
        } else {
            setActiveTab('home');
        }
    }, [location.pathname]);

    const renderContent = () => {
        switch (activeTab) {
            case 'home':
                return <Home />;
            case 'all-courses':
                return <AllCourses />;
            case 'telemetry':
                return <TelemetryPage />;
            case 'home-health':
                return <HomeHealth />;
            default:
                return <Home />;
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'white',
            color: '#4a306d',
            fontFamily: 'Arial, sans-serif'
        }}>
            {/* Simple Header with Logo Left and Tabs Right */}
            <header style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem 2rem',
                background: 'white'
            }}>
                {/* Logo on the left - clickable to go home */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer'
                    }}
                    onClick={() => {
                        setActiveTab('home');
                        navigate('/');
                    }}
                >
                    <img
                        src="/tdl-logo.jpg"
                        alt="TDL Coaching & Consulting"
                        style={{
                            height: '50px',
                            width: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                </div>

                {/* Navigation Tabs on the right */}
                <nav style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
                    {[
                        { id: 'all-courses', label: 'All Courses' },
                        { id: 'telemetry', label: 'Telemetry' },
                        { id: 'home-health', label: 'Home Health Aide' }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => {
                                setActiveTab(tab.id);
                                if (tab.id === 'all-courses') {
                                    navigate('/all-courses');
                                } else if (tab.id === 'telemetry') {
                                    navigate('/telemetry');
                                } else if (tab.id === 'home-health') {
                                    navigate('/home-health');
                                }
                            }}
                            style={{
                                padding: '0.5rem 1rem',
                                border: 'none',
                                background: 'transparent',
                                color: activeTab === tab.id ? '#34a8a8' : '#4a306d',
                                fontSize: '1rem',
                                fontWeight: activeTab === tab.id ? '500' : '400',
                                cursor: 'pointer',
                                transition: 'color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                if (activeTab !== tab.id) {
                                    e.currentTarget.style.color = '#34a8a8';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (activeTab !== tab.id) {
                                    e.currentTarget.style.color = '#4a306d';
                                }
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </header>

            {/* Content Area */}
            <main>
                {renderContent()}
            </main>

            {/* Common Footer */}
            <footer style={{
                background: '#4a306d',
                padding: '3rem 0 1rem 0',
                borderTop: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 40px' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '2rem',
                        marginBottom: '2rem',
                        alignItems: 'start'
                    }}>
                        {/* Logo */}
                        <div>
                            <img
                                src="/tdl-logo.jpg"
                                alt="TDL Coaching & Consulting"
                                style={{
                                    maxWidth: '180px',
                                    height: 'auto',
                                    display: 'block'
                                }}
                            />
                        </div>

                        {/* Contact Information */}
                        <div>
                            <h3 style={{
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                marginBottom: '1rem',
                                marginTop: 0,
                                color: 'white'
                            }}>
                                Contact Information
                            </h3>
                            <p style={{ margin: '0.5rem 0', color: 'white', fontSize: '0.95rem' }}>
                                Clearwater Fl 33760
                            </p>
                            <p style={{ margin: '0.5rem 0' }}>
                                <a
                                    href="mailto:info@tdlcoaching.com"
                                    style={{
                                        color: 'white',
                                        textDecoration: 'underline',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    info@tdlcoaching.com
                                </a>
                            </p>
                            <p style={{ margin: '0.5rem 0', color: 'white', fontSize: '0.95rem' }}>
                                1-888-702-7428
                            </p>
                        </div>

                        {/* Hours of Operation */}
                        <div>
                            <h3 style={{
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                marginBottom: '1rem',
                                marginTop: 0,
                                color: 'white'
                            }}>
                                Hours of Operation
                            </h3>
                            <p style={{ margin: '0.5rem 0', color: 'white', fontSize: '0.95rem' }}>
                                Mon-Fri: 9-5pm
                            </p>
                            <p style={{ margin: '0.5rem 0', color: 'white', fontSize: '0.95rem' }}>
                                Sat: 10-4pm
                            </p>
                            <p style={{ margin: '0.5rem 0', color: 'white', fontSize: '0.95rem' }}>
                                Sun: Closed
                            </p>
                        </div>

                        {/* Courses List */}
                        <div>
                            <h3 style={{
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                marginBottom: '1rem',
                                marginTop: 0,
                                color: 'white'
                            }}>
                                Courses List
                            </h3>
                            <p style={{ margin: '0.5rem 0' }}>
                                <button
                                    onClick={() => navigate('/telemetry')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        color: 'white',
                                        textDecoration: 'underline',
                                        fontSize: '0.95rem',
                                        cursor: 'pointer',
                                        fontFamily: 'inherit'
                                    }}
                                >
                                    Telemetry/EKG
                                </button>
                            </p>
                            <p style={{ margin: '0.5rem 0' }}>
                                <button
                                    onClick={() => navigate('/home-health')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        color: 'white',
                                        textDecoration: 'underline',
                                        fontSize: '0.95rem',
                                        cursor: 'pointer',
                                        fontFamily: 'inherit'
                                    }}
                                >
                                    Home Health Aide
                                </button>
                            </p>
                            <p style={{ margin: '0.5rem 0' }}>
                                <button
                                    onClick={() => navigate('/all-courses')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        color: 'white',
                                        textDecoration: 'underline',
                                        fontSize: '0.95rem',
                                        cursor: 'pointer',
                                        fontFamily: 'inherit'
                                    }}
                                >
                                    CPR Training
                                </button>
                            </p>
                        </div>

                        {/* Follow */}
                        <div>
                            <h3 style={{
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                marginBottom: '1rem',
                                marginTop: 0,
                                color: 'white'
                            }}>
                                Follow
                            </h3>
                            <p style={{ margin: '0.5rem 0' }}>
                                <a
                                    href="https://www.tdlcoaching.com/testimoniesclients"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: 'white',
                                        textDecoration: 'underline',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    Testimonials
                                </a>
                            </p>
                            <div style={{ marginTop: '1rem' }}>
                                <a
                                    href="https://www.facebook.com/groups/328411097271703"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: '#000',
                                        borderRadius: '50%',
                                        color: 'white',
                                        textDecoration: 'none',
                                        fontSize: '1.2rem',
                                        fontWeight: 'bold'
                                    }}
                                    aria-label="Facebook"
                                >
                                    f
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Copyright */}
                    <div style={{
                        textAlign: 'center',
                        paddingTop: '1.5rem',
                        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontSize: '0.9rem'
                    }}>
                        © 2025 TDL Coaching. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default MainPage;